import { IconChevronDown } from "@hawkins/assets";
import * as Accordion from "@radix-ui/react-accordion";
import cx from "classnames";
import { ResultOf } from "gql.tada";
import { ComponentProps, ElementRef, forwardRef } from "react";
import { SolutionsRowFragment } from "../../../queries/entries/SolutionsSectionById";
import { RichText } from "../../shared/core/RichText";
import { MarkdownLite } from "../../shared/MarkdownLite";

type SolutionsChapterRowPrimitiveProps = ComponentProps<typeof Accordion.Item>;
type SolutionsChapterRowProps = SolutionsChapterRowPrimitiveProps & {
  row: ResultOf<typeof SolutionsRowFragment>;
};

const SolutionsChapterRow = forwardRef<
  ElementRef<typeof Accordion.Item>,
  SolutionsChapterRowProps
>(({ row, ...props }, forwardRef) => {
  if (row.rowType === "Section Title") {
    return (
      <div
        key={row._id}
        className="dsk:mt-[60px] mt-5 mb-5 flex flex-col dsk:gap-10 gap-7"
      >
        <p className="text-brand-cream text-body-regular">
          {row?.sectionTitleEyebrow}
        </p>
        <h3 className="text-heading-4">{row?.sectionTitle}</h3>
      </div>
    );
  }

  return (
    <Accordion.Item
      key={row._id}
      className="mt-px overflow-hidden first:mt-0 focus-within:relative focus-within:z-10 pb-[38px] text-brand-cream"
      ref={forwardRef}
      disabled={row.collapsible === false}
      {...props}
    >
      <Accordion.Header className="flex pb-[22px]">
        <Accordion.Trigger
          className={cx(
            "group flex flex-1 justify-between outline-none items-start h-max pt-10 flex-col cursor-pointer",
            "dsk:grid dsk:grid-cols-[repeat(17,_minmax(0,_1fr))] dsk:gap-x-4 dsk:grid-flow-row-dense",
            row.collapsible === false && "!cursor-default"
          )}
        >
          <div
            className={cx(
              "w-full flex justify-between pb-[38px] dsk:pb-0 text-left",
              "dsk:col-span-5"
            )}
          >
            <p className="text-body-large">{row?.sectionTitleEyebrow}</p>
            <IconChevronDown
              className={cx(
                "transition-transform duration-300 group-data-[state=open]:rotate-180 mb-auto text-brand-cream dsk:hidden",
                !row.collapsible && "hidden"
              )}
              aria-hidden
            />
          </div>
          <div className="w-full dsk:col-span-9 dsk:col-start-7">
            <h4 className="text-heading-4 text-left">
              <MarkdownLite>{row?.sectionTitle}</MarkdownLite>
            </h4>
          </div>
          <div className="dsk:col-span-1 dsk:col-start-16 hidden dsk:block">
            <IconChevronDown
              className={cx(
                "transition-transform duration-300 group-data-[state=open]:rotate-180 mb-auto text-brand-cream",
                row.collapsible === false && "hidden"
              )}
              aria-hidden
            />
          </div>
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content
        className={cx(
          "overflow-hidden data-[state=closed]:animate-accordionUp data-[state=open]:animate-accordionDown"
        )}
      >
        <div className="dsk:grid dsk:grid-cols-[repeat(17,_minmax(0,_1fr))] dsk:gap-x-4 dsk:grid-flow-row-dense h-auto">
          <div className="dsk:col-span-9 dsk:col-start-7">
            <div className="text-brand-cream">
              <RichText content={row.bodyRichText} />
            </div>
          </div>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
});

const SolutionsChapterRows = ({
  rows,
  hasMedia,
}: {
  rows: ResultOf<typeof SolutionsRowFragment>[];
  hasMedia: boolean;
}) => {
  if (!rows.length) return null;

  return (
    <Accordion.Root
      className={cx("w-full", !hasMedia && "mt-[100px]")}
      type="multiple"
      defaultValue={rows.map(({ _id }) => _id)}
    >
      {rows?.map((row) => (
        <SolutionsChapterRow key={row._id} row={row} value={row._id} />
      ))}
    </Accordion.Root>
  );
};

export default SolutionsChapterRows;
